import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  Button,
  Label,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import weightageIcon from '../../../../assets/images/performance/weightageIcon.png';
import { initialValues, validationSchema } from './constant';
import { addWeightage } from '../../../../store/apis/performanceService';
import { getPerformanceWeightage } from '../../common';

function AddWeightage({
  selectedKPI,
  edit = false,
  refetchPerformanceDetails,
  selectedEmployee,
  pcmWeigtage,
  performanceWeigtage,
  weightagePercentage,
}) {
  const comPCM = pcmWeigtage - Number(selectedKPI?.Weightage);
  const comPRF = performanceWeigtage - Number(selectedKPI?.Weightage);
  const [modalWeightage, setModalWeightage] = useState(false);
  const {
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
  });

  useEffect(() => {
    reset();
    if (selectedKPI?.Weightage) {
      setValue('weightage', Number(selectedKPI.Weightage));
    }
  }, [reset, selectedKPI, setValue]);

  const { mutate: saveWeightage, isPending } = useMutation({
    mutationFn: addWeightage,
    onSuccess: () => {
      toast.success('Weightage added successfully!', {
        className: 'toast-success',
      });
      refetchPerformanceDetails();
    },
    onError: error => {
      toast.error(error?.message || 'Failed to add weightage', {
        className: 'toast-error',
      });
    },
  });

  const toggleWeightage = e => {
    e?.stopPropagation();
    setModalWeightage(!modalWeightage);
  };

  const onSubmit = data => {
    if (selectedKPI) {
      const performanceWeightage =
        getPerformanceWeightage(weightagePercentage)?.performanceWeightage;
      const pcmWeightage =
        getPerformanceWeightage(weightagePercentage)?.pcmWeightage;

      if (
        selectedKPI.KPI_Type === 'PCM' &&
        comPCM + Number(data.weightage) > pcmWeightage
      ) {
        toast.error(`Total PCM weightage cannot exceed ${pcmWeightage}%`, {
          className: 'toast-error',
        });
        return;
      }
      if (
        selectedKPI.KPI_Type === 'PERFORMANCE' &&
        comPRF + Number(data.weightage) > performanceWeightage
      ) {
        toast.error(
          `Total Performance weightage cannot exceed ${performanceWeightage}%`,
          {
            className: 'toast-error',
          },
        );
        return;
      }
    }
    const payload = {
      kpiCategory: selectedKPI?.KPI_Category,
      kpiSeq: selectedKPI?.KPI_Sequence,
      weightage: data.weightage,
      employeeId: selectedEmployee?.value,
      userId: '',
    };
    saveWeightage(payload);
    toggleWeightage();
    reset();
  };

  return (
    <div>
      <Button
        className="btn-primary primary actionItem fs-8"
        style={{ height: '28px' }}
        onClick={toggleWeightage}
      >
        {edit ? 'Edit Weightage' : 'Add Weightage'}
      </Button>
      <Modal
        isOpen={modalWeightage}
        toggle={toggleWeightage}
        style={{ maxWidth: '500px', width: '100%' }}
        centered
        backdrop="static"
        keyboard={false}
      >
        <ModalHeader toggle={toggleWeightage}>
          <div className="addKpiHeader">
            <img alt="" src={weightageIcon} />
            <div className="title">
              {edit ? 'Edit Weightage' : 'Add Weightage'}
            </div>
          </div>
        </ModalHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <div className="modalWeightageBody">
              <Label>
                Weightage (%)
                <span className="text-danger"> *</span>
              </Label>
              <Controller
                name="weightage"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    className="external-filter-input form-control"
                    type="text"
                    placeholder="Enter"
                    title="Enter the Weightage. Weightages defined for the KPIs should be within the alotted weightage for the category (Performance and PCM)"
                  />
                )}
              />
              {errors.weightage && (
                <span className="text-danger">{errors.weightage.message}</span>
              )}
            </div>
          </ModalBody>
          <ModalFooter className="modalFooter">
            <Button
              className="item custom-light-btn"
              onClick={toggleWeightage}
              type="button"
            >
              Cancel
            </Button>
            <Button
              className="item custom-btn"
              type="submit"
              disabled={!isValid || isPending}
            >
              {isPending ? 'Saving...' : 'Save'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
}

export default AddWeightage;
